<script lang="ts" setup>
  import { format } from 'date-fns'
  import { es, fr, it, de } from 'date-fns/locale'
  import { refDebounced } from '@vueuse/core'
  import { CalendarDate, today, getLocalTimeZone } from '@internationalized/date'
  import type { DateValue } from '@internationalized/date'
  import { dateToCalendarDate, calendarDateToDate, isDateMarked } from '~/utils/calendar'

  // Scroll Store
  const scroll = useScrollStore()
  const { isScrolledInput } = storeToRefs(scroll)

  // Currency Store
  const currencyStore = useCurrencyStore()
  const { currency } = storeToRefs(currencyStore)

  // Idioma
  const { locale, t } = useI18n()

  // Config
  const runtimeConfig = useRuntimeConfig()
  
  // Home Store
  const { isHome } = useIsHome()

  // Loading (navegación / cambio de idioma)
  const { isLoading } = useLoadingIndicator()
  const showSkeletonTransition = ref(false)

  // Mostrar skeleton al cambiar idioma o moneda (evita flash antes de que isLoading sea true)
  watch([locale, currency], () => {
    showSkeletonTransition.value = true
  }, { immediate: false })

  watch(isLoading, (loading) => {
    if (!loading) showSkeletonTransition.value = false
  })

  const showHeaderSkeleton = computed(() => isLoading.value || showSkeletonTransition.value)

  // Ubicacion
  const selectedDestStore = useDestinationStore()
  const { selectedDestination, labelDestination, cookieDestination, hotelDispo } = storeToRefs(selectedDestStore)

  // Evitar hydration mismatch: el icono del input (map-pin vs loader-circle) solo
  // refleja loading después de montar, así servidor y cliente renderizan igual.
  const isMounted = ref(false)
  onMounted(() => {
    isMounted.value = true
    window.addEventListener('scroll', scroll.checkScrollInput)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('scroll', scroll.checkScrollInput)
  })

  // Fecha
  const selectedDiaStore = useSelectedDayStore()
  const { selectedDay } = storeToRefs(selectedDiaStore)

  watch(selectedDay, (newValue) => {
    if (newValue) {
      selectedDiaStore.setDay(newValue)
    }
    isOpenSearch.value = false
    openCalendar.value = false
    openCalendarPopover.value = false
    if(mobileButton.value){
      buscar()
    }
  })

  // CalendarDate conversion for UCalendar
  const calendarDate = computed({
    get: (): DateValue => {
      const cd = selectedDay.value ? dateToCalendarDate(selectedDay.value) : null
      return cd || today(getLocalTimeZone())
    },
    set: (value: DateValue | null) => {
      if (value && 'year' in value) {
        const date = calendarDateToDate(value as CalendarDate)
        if (date) {
          selectedDiaStore.setDay(date)
        }
      }
    }
  })

  // Min date for calendar (today)
  const minCalendarDate = computed<DateValue>(() => today(getLocalTimeZone()))

  const selectHoy = () => {
    calendarDate.value = today(getLocalTimeZone())
    openCalendar.value = false
    openCalendarPopover.value = false
  }
  const selectMañana = () => {
    calendarDate.value = today(getLocalTimeZone()).add({ days: 1 })
    openCalendar.value = false
    openCalendarPopover.value = false
  }
  
  const locales: any = { es, fr, it, de }
  const formatDatePicker = (date: any) => {
    date = new Date(date)
    const day = format(date, 'dd', { locale: locales[locale.value] })
    const month = format(date, 'MMMM', { locale: locales[locale.value] })
    if (locale.value === 'es') {
      return `${day} de ${month}`
    } else if (locale.value === 'it' || locale.value === 'fr' || locale.value === 'de') {
      return `${day} ${month}`
    } else {
      return `${month} ${day}`
    }
  }

  // Ubicacion
  const searchTerm = ref('')
  const searchTermDebounced = refDebounced(searchTerm, 200)
  const labelDestino: any = ref(selectedDestination.value)
  watch(selectedDestination, (newVal) => {
    labelDestino.value = newVal
  })
  // Search Modals Store
  const searchModalsStore = useSearchModalsStore()
  const { openDest, openCalendar } = storeToRefs(searchModalsStore)
  
  const openCalendarPopover = ref(false)
  const openDestInput = ref(false)
  const mobileButton = ref(false)

  const route = useRoute()

  // Preservar scroll al cerrar el modal del calendario (evita que el footer suba en desktop)
  // En la página de búsqueda no restauramos para que al cambiar ubicación y cerrar el calendario se mantenga el scroll arriba
  const scrollPositionWhenCalendarOpened = ref<{ x: number; y: number } | null>(null)
  watch(openCalendar, (isOpen) => {
    if (isOpen) {
      scrollPositionWhenCalendarOpened.value = { x: window.scrollX, y: window.scrollY }
    } else if (scrollPositionWhenCalendarOpened.value !== null) {
      const { x, y } = scrollPositionWhenCalendarOpened.value
      scrollPositionWhenCalendarOpened.value = null
      const isSearchPage = route.path.includes('search')
      if (!isSearchPage) {
        // Restaurar después de que el modal haya cerrado (animación + liberación del body)
        setTimeout(() => {
          window.scrollTo(x, y)
        }, 100)
      }
    }
  })
  
  // Ubicacion Actual
  const labelUbicacionActual = t('labels.ubicacionActual')
  const destinoActualLocation = ref('')
  const destinoActual = computed(() => {
    return [
      {
        id: null,
        label: labelUbicacionActual,
        name_translated: labelUbicacionActual,
        location: destinoActualLocation.value,
        icon: 'i-ic-round-near-me',
      }
    ]
  })
  
  const { data: destinations, status: pendingDest } = await useFetch<{ data: Autocomplete[] }>(runtimeConfig.public.API_CLIENT_BASE_URL + '/autocomplete', {
    headers: { "Content-Type": "application/json" },
    query: { 
      term: searchTermDebounced,
      limit: 10,
      locale
    },
    cache: 'no-store',
    server: false,
    lazy: true,
    transform: (data: { data: Autocomplete[] }) => {
      const destinationData = data?.data || []
      const dataDestination = destinationData?.map(destination => ({
        id: destination?.element_id, 
        label: destination?.name_translated, 
        suffix: destination?.destination_name ? `${destination?.destination_name}, ${destination?.country_name_translated}` : destination?.country_name_translated,
        icon: destination?.type === 'hotel' ? 'i-lucide-building-2' : 
              destination?.type === 'destination' ? 'i-heroicons-map-pin-solid' : 
              destination?.type === 'place' ? 'i-lucide-locate-fixed' : 
              'ic:round-near-me',
        ...destination
      })) || []
      return { data: dataDestination }
    }
  })

  const groupUbicacion = {
    id: 'actions',
    items: destinoActual.value,
    ignoreFilter: true,
  }

  const groups = computed(() => [
    groupUbicacion,
    {
      id: 'autocomplete',
      label: `${t('labels.searchDestino')} “${searchTerm.value}”...`,
      items: destinations.value?.data || [],
      ignoreFilter: true
    }
  ])

  const defaultDestination = ref([
    {
      id: 'top-destinations',
      label: t('labels.destinosPopulares'),
      items: defaultDestinations[locale.value] || [],
    }
  ])

  const groupsDefault = computed(() => [
    groupUbicacion,
    {
      id: 'default-destinations',
      label: t('labels.destinosPopulares'),
      items: defaultDestination.value[0]?.items || [],
      ignoreFilter: true
    }
  ])

  const groupsEmpty = computed(() => [
    {
      id: 'empty',
      items: [],
      ignoreFilter: true
    }
  ])

  // Computed list of items for the location input dropdown
  const itemsInput: any = computed(() => {
    if (pendingDest.value === 'pending') {
      return {
        id: 'loading',
        label: 'Cargando...',
        type: 'loading' as const
      }
    }
    if (destinations.value?.data?.length) {
      return [
        ...destinoActual.value,
        { type: 'separator' as const },
        ...destinations.value.data.map(item => ({ ...item, type: 'item' as const }))
      ]
    }
    return []
  })

  const toast = useToast()
  function onSelect(item: any) {
    if(!item?.id){
      selectedDestStore.getLocation((res: { err?: string; data?: string }) => {
        if(res.err){
          toast.add({ title: res.err })
          searchTerm.value = ''
        }else{
          if (item) {
            item.location = res.data ?? ''
            destinoActualLocation.value = res.data ?? ''
          }
          selectedDestination.value = item
          openDest.value = false
          openCalendar.value = true
        }
      })
    }else{
      selectedDestination.value = item
      openDest.value = false
      openCalendar.value = true
    }
  }

  function onSelectInput(item: any) {
    if (!item?.id) {
      selectedDestStore.getLocation((res: { err?: string; data?: string }) => {
        if (res.err) {
          toast.add({ title: res.err })
          clearInput()
          openDestInput.value = false
        } else {
          if (item) {
            item.location = res.data ?? ''
            destinoActualLocation.value = res.data ?? ''
          }
          selectedDestination.value = item 
          openDestInput.value = false
        }
      })
    }else{
      selectedDestination.value = item
    }
  }

  function clearInput() {
    if(selectedDestination.value){
      if(labelDestination.value){
        labelDestino.value = null
      }
    }
  }

  // Search
  const isOpenSearch = ref(false)
  const disableSearch = ref(false)
  const buscar = async () => {
    if(!objIsEmpty(selectedDestination.value ?? {}) && labelDestino.value){
      isOpenSearch.value = false
      const regexHotel = /(?<=\/[a-z]{2}\/)[^\/]+(?=\.html)/
      const matchHotel = selectedDestination?.value?.['url']?.match(regexHotel)
      const slug = matchHotel ? matchHotel[0] : ''
      const hotelPath = `/${locale.value}/${slug}.html`
      let path = cookieDestination.value?.category_id == 1 ? hotelPath : `/${locale.value}/search` // si es hotel, va a landing hotel
      disableSearch.value = true
      const [latitude, longitude] = cookieDestination.value?.location ? cookieDestination.value.location.split(',') : [0, 0]

      await navigateTo({
        path,
        query: {
          'data-destinationId': cookieDestination.value?.category_id === 3 ? '' : cookieDestination.value?.element_id,
          'data-longitude': longitude,
          'data-latitude': latitude,
          'data-distance': cookieDestination.value?.search_distance,
          'data-search': cookieDestination.value?.name_translated,
          'data-country': cookieDestination.value?.country_name_translated,
          'search': cookieDestination.value?.name_translated,
          'data-checkinDate': selectedDay.value ? formatDateGuion(selectedDay.value) : '',
          'GFakeDate': selectedDay.value ? formatFakeDate(selectedDay.value) : '',
          'curr': currency.value
        }
      })
    }else{
      openDest.value = true
    }
  }

  // Device
  const deviceStore = useDeviceStore()
  const isMobile = deviceStore.isMobile

  // Border round
  const appConfig = useAppConfig()
  const app_round = appConfig.ui.button.slots.base

</script>

<template>

  <!-- Ubicacion -->
  <UModal
    v-model:open="openDest"
    :fullscreen="isMobile"
    :class="isMobile ? 'm-4 border border-neutral-200 rounded-xl h-fit shadow-2xl' : ''"
    :title="t('labels.ubicacion')"
    :description="t('labels.ubicacion')"
  >
    <template #content>
      <UCommandPalette
        @update:model-value="onSelect"
        v-model:search-term="searchTerm"
        :groups="searchTerm?.length > 2 ? destinations?.data?.length ? groups : groupsEmpty : groupsDefault"
        :loading="pendingDest === 'pending'"
        icon="i-lucide-map-pin"
        :placeholder="t('labels.placeholderDestino')"
        :ui="{
          content: 'min-h-92 h-full'
        }"
      >
        <template #empty>
          <div v-if="pendingDest === 'pending'" class="min-h-72 h-full flex justify-center items-center">
            <UIcon name="i-lucide:loader-circle" class="animate-spin text-lg mr-2" />
            <span>{{ $t('buttons.cargando') }}</span>
          </div>
          <UEmpty
            v-else
            :title="$t('labels.errorResultadoTitle')"
            :description="$t('labels.errorResultado')"
            icon="i-lucide-search"
            variant="naked"
            :actions="[{
              label: $t('labels.ubicacionActual'),
              icon: 'i-ic-round-near-me',
              color: 'neutral',
              variant: 'solid',
              onClick: () => onSelect(destinoActual[0])
            }]"
          />
        </template>
      </UCommandPalette>
    </template>
  </UModal>

  <!-- Calendario -->
  <UModal
    v-model:open="openCalendar"
    :ui="{ content: 'sm:w-fit ring-0 border border-neutral-200 rounded-lg' }"
    title="Calendario"
    description="Calendario"
  >
    <template #content>
      <div class="p-4">
        <UCalendar
          v-model="calendarDate"
          :min-value="minCalendarDate"
          color="neutral"
          size="lg"
          :year-controls="false"
        >
          <template #heading="{ value }">
            {{ value.charAt(0).toUpperCase() + value.slice(1) }}
          </template>
          <template #day="{ day }">
            <UChip 
              :show="isDateMarked(day, hotelDispo)" 
              color="success"
              size="2xs"
            >
              {{ day.day }}
            </UChip>
          </template>
        </UCalendar>
        <div class="flex justify-center gap-4 mt-4">
          <UButton size="lg" :aria-label="$t('labels.hoy')" block color="neutral" variant="outline" @click="selectHoy()">{{ $t('labels.hoy') }}</UButton>
          <UButton size="lg" :aria-label="$t('labels.mañana')" block color="neutral" variant="outline" @click="selectMañana()">{{ $t('labels.mañana') }}</UButton>
        </div>
      </div>
    </template>
  </UModal>

  <!-- Hero Search Input -->
  <div v-if="!isScrolledInput && isHome">
    <div :class="`relative h-full`">
      <div :class="`w-full flex flex-col md:flex-row gap-2 md:bg-white p-1 rounded-full items-center md:shadow hover:shadow-md transition-all duration-300`">

        <!-- Ubicacion -->
        <UButton
          @click="openDest = true; mobileButton = false"
          :label="selectedDestination?.name_translated ?? t('labels.ubicacion')"
          color="neutral"
          variant="link"
          icon="i-lucide-map-pin"
          size="lg"
          :ui="{ 
            base: 'w-full md:min-w-72 md:max-w-72 bg-white'
          }"
        />

        <USeparator class="hidden md:block h-6 w-0.5 bg-neutral-200" />

        <!-- Calendario -->
        <UButton
          @click="openCalendar = true"
          :label="formatDatePicker(selectedDay)"
          color="neutral"
          variant="link"
          icon="i-lucide-calendar"
          size="lg"
          :ui="{ 
            base: 'w-full md:pr-10 md:min-w-52 md:max-w-52 bg-white'
          }"
        />

        <!-- Search -->
        <UButton
          :disabled="disableSearch"
          class="bg-linear-to-r from-primary-400 to-pink-600"
          block
          :aria-label="$t('buttons.buscar')"
          @click="buscar()"
          size="lg"
          :label="$t('buttons.buscar')"
        >
          <template #leading>
            <UIcon v-if="!disableSearch" class="text-xl min-w-5" name="i-lucide-search" />
            <svg v-else class="animate-spin h-5 min-w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </template>
        </UButton>
      </div>
    </div>
  </div>

  <!-- Header Input md -->
  <div v-else :class="`w-full sm:w-[450px]`">
    <div class="hidden sm:flex">
      <!-- Input ubicación, calendario y buscar -->
      <div v-if="!showHeaderSkeleton" :class="`truncate w-full flex gap-2 border border-neutral-200 bg-white ${app_round} items-center p-0.5 hover:shadow-sm transition-all duration-300 justify-between`">
        <!-- Ubicacion -->
        <UInputMenu
          v-model="labelDestino"
          labelKey="name_translated"
          v-model:search-term="searchTerm"
          v-model:open="openDestInput"
          @update:model-value="onSelectInput"
          :items="(itemsInput as any) || []"
          :loading="isMounted && pendingDest === 'pending'"
          icon="i-lucide-map-pin"
          :placeholder="t('labels.ubicacion')"
          class="truncate"
          ignore-filter
          size="md"
          trailing-icon=""
          variant="none"
          :ui="{
            base: 'w-62 text-toned truncate hover:text-default',
            content: 'mt-2 max-h-fit w-fit min-w-96',
            leadingIcon: 'text-toned',
          }"
          :content="{
            align: 'start',
          }"
          @click="clearInput()"
        >
          <template #item-label="{ item }">
            {{ item.label }}

            <span v-if="item.destination_name" class="text-dimmed">
              {{ item.destination_name }},
            </span>

            <span class="text-dimmed">
              {{ item.country_name_translated }}
            </span>
          </template>
          <template #empty>
            <div v-if="pendingDest === 'pending'" class="min-h-80 h-full flex justify-center items-center">
              <UIcon name="i-lucide:loader-circle" class="animate-spin text-lg mr-2" />
              <span>{{ $t('buttons.cargando') }}</span>
            </div>
            <UEmpty
              v-else
              :title="$t('labels.errorResultadoTitle')"
              :description="$t('labels.errorResultado')"
              icon="i-lucide-search"
              variant="naked"
              size="sm"
              :actions="[{
                label: $t('labels.ubicacionActual'),
                icon: 'i-ic-round-near-me',
                color: 'neutral',
                variant: 'solid',
                size: 'xs',
                onClick: () => onSelectInput(destinoActual[0])
              }]"
            />
          </template>
        </UInputMenu>

        <USeparator class="hidden md:block h-6 w-0.5 bg-neutral-200" />

        <!-- Calendario -->
        <UPopover v-model:open="openCalendarPopover" :ui="{ content: 'ring-0 border border-neutral-200 rounded-lg' }">
          <UButton
            :label="formatDatePicker(selectedDay)"
            color="neutral"
            variant="link"
            icon="i-lucide-calendar"
            size="md"
            :ui="{ 
              base: 'md:pr-4 bg-white'
            }"
          />

          <template #content>
            <div class="p-4">
              <UCalendar
                v-model="calendarDate"
                :min-value="minCalendarDate"
                color="neutral"
                size="md"
                :year-controls="false"
              >
                <template #heading="{ value }">
                  {{ value.charAt(0).toUpperCase() + value.slice(1) }}
                </template>
                <template #day="{ day }">
                  <UChip 
                    :show="isDateMarked(day, hotelDispo)"
                    color="success"
                    size="2xs"
                  >
                    {{ day.day }}
                  </UChip>
                </template>
              </UCalendar>
              <div class="flex justify-center gap-4 mt-4">
                <UButton size="sm" :aria-label="$t('labels.hoy')" block color="neutral" variant="outline" @click="selectHoy()">{{ $t('labels.hoy') }}</UButton>
                <UButton size="sm" :aria-label="$t('labels.mañana')" block color="neutral" variant="outline" @click="selectMañana()">{{ $t('labels.mañana') }}</UButton>
              </div>
            </div>
          </template>
        </UPopover>

        <!-- Search -->
        <div>
          <UButton
            class="bg-linear-to-r from-primary-500 to-pink-500"
            :aria-label="$t('buttons.buscar')"
            @click="buscar()"
            icon="i-lucide-search"
            size="lg"
          />
        </div>
      </div>
    </div>
  </div>

  <!-- Header Button -->
  <div v-if="isScrolledInput || !isHome" class="sm:hidden w-full max-w-full min-w-0 overflow-hidden sm:w-[500px]">
    <!-- Botones destino y fecha -->
    <div v-if="!showHeaderSkeleton" class="table table-fixed w-full max-w-full">
      <div class="table-cell w-1/2 align-middle overflow-hidden">
        <UButton size="sm" :ui="{ base: 'rounded-full rounded-r-none justify-center w-full' }" @click="openDest = true; mobileButton = true" color="neutral" variant="outline">
          <span class="block min-w-0 truncate text-left">{{ labelDestination || '🗺️' }}</span>
        </UButton>
      </div>
      <div class="table-cell w-1/2 align-middle overflow-hidden">
        <UButton size="sm" :ui="{ base: 'rounded-full rounded-l-none justify-center w-full' }" @click="openCalendar = true;" color="neutral" variant="outline">
          <span class="block min-w-0 truncate">{{ formatDatePicker(selectedDay) }}</span>
        </UButton>
      </div>
    </div>
  </div>

</template>
